import { Cart, CartDetails } from '@/modules/store/models/Cart';
import { CartItem, CartItemDetails } from '@/modules/store/models/CartItem';
import { CalculatedPrice } from '@/modules/store/models/Price';
import { Sku } from '@/modules/store/models/Sku';
import { Supplier } from '@/modules/store/models/Supplier';
import CartPriceService from '@/modules/store/services/CartPriceService';
import ProductPriceService from '@/modules/store/services/ProductPriceService';

class CartDetailsService {
  private readonly cartPriceService: CartPriceService;
  private readonly productPriceService: ProductPriceService;

  constructor(
    cartPriceService = new CartPriceService(),
    productPriceService = new ProductPriceService(),
  ) {
    this.cartPriceService = cartPriceService;
    this.productPriceService = productPriceService;
  }

  /**
   * Builds all CartDetails from multiple carts.
   */
  getCartDetails(
    carts: Cart[],
    skus: Sku[],
    suppliers: Supplier[],
    currencyCode: string,
  ): CartDetails[] {
    const result: CartDetails[] = [];

    for (const cart of carts) {
      const supplier = suppliers.find((s) => s.id === cart.supplierId);
      if (!supplier) {
        console.error('Supplier not found for cart', cart);
        continue;
      }

      const details = this.getCartDetailsForCart(cart, skus, supplier, currencyCode);
      if (details) {
        result.push(details);
      }
    }

    return result;
  }

  /**
   * Builds CartDetails for a single supplier cart.
   */
  getCartDetailsForCart(
    cart: Cart,
    skus: Sku[],
    supplier: Supplier,
    currencyCode: string,
  ): CartDetails | undefined {
    const allItemDetails: CartItemDetails[] = [];

    for (const cartItem of cart.items) {
      const sku = skus.find((sku) => sku.id === cartItem.skuId);
      if (!sku) {
        console.error('SKU not found for cart item', cartItem);
        continue;
      }

      const itemDetails = this.getCartItemDetails(cartItem, sku, currencyCode);
      if (itemDetails) {
        allItemDetails.push(itemDetails);
      }
    }

    if (allItemDetails.length === 0) {
      console.error('No items found in cart', cart);
      return undefined;
    }

    const subtotal =
      this.cartPriceService.calculateSubtotal(allItemDetails) || this.createZeroPrice(currencyCode);
    const grandTotal =
      this.cartPriceService.calculateGrandTotal(allItemDetails) ||
      this.createZeroPrice(currencyCode);

    return {
      deliveryWindow: cart.deliveryWindow,
      pickupWindow: cart.pickupWindow,
      currencyCode,
      supplierId: cart.supplierId,
      supplierName: supplier.name,
      items: allItemDetails,
      moq: supplier.moq || 0,
      totalQuantity: cart.totalQuantity,
      subtotal,
      grandTotal,
    };
  }

  /**
   * Converts a raw CartItem + SKU into a fully enriched CartItemDetails.
   */
  private getCartItemDetails(
    cartItem: CartItem,
    sku: Sku,
    currencyCode: string,
  ): CartItemDetails | undefined {
    const unitPrice = this.productPriceService.calculateUnitPrice(sku);
    const lineItemPrice = this.productPriceService.calculateLineItemPrice(sku, cartItem.quantity);

    const vatRate = sku.product?.vatRate || 0;
    const vatAmount = (unitPrice?.amount || 0) * (vatRate / 100);

    return {
      id: cartItem.id,
      supplierId: cartItem.supplierId,
      skuId: cartItem.skuId,
      sku: {
        code: sku.code,
        externalCode: sku.externalCode,
        name: sku.name,
        image: sku.images[0],
        prices: sku.prices || [],
        promotions: sku.promotions || [],
        moq: sku.moq || 0,
        status: sku.status,
      },
      quantity: cartItem.quantity,
      price: unitPrice,
      unitPrice,
      lineItemPrice,
      vatAmount: {
        currencyCode: unitPrice?.currencyCode || currencyCode,
        amount: vatAmount,
      },
      vatRate,
      prediction: cartItem.prediction,
      checked: cartItem.checked,
    };
  }

  private createZeroPrice(currencyCode: string): CalculatedPrice {
    const originalPrice = {
      currencyCode,
      amount: 0,
    };

    return {
      ...originalPrice,
      originalPrice,
    };
  }
}

export default CartDetailsService;
